import { useState, useEffect } from 'react';
import { usePermissions } from '@/contexts/unified-permissions';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { ReferralStorageService } from '@/services/referral/ReferralStorageService';
import { validateEmail } from '@/utils/validation';
import { classifyAuthError, formatAuthError } from '@/services/auth/errorHandling';



export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get permissions state for redirect logic
  const { isAuthenticated, authReady, signIn } = usePermissions();

  // Redirect authenticated users to journal once auth is ready (don't wait for permissions)
  useEffect(() => {
    if (authReady && isAuthenticated) {
      // If there's a redirect path from protected routes, use it
      const from = location.state?.from?.pathname || '/journal';
      navigate(from);
    }
  }, [authReady, isAuthenticated, navigate, location.state?.from?.pathname]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setResendSuccess(false);
    setResetSuccess(false);
    setIsSigningIn(true);

    if (!email || !password) {
      setError('Email and password are required');
      setIsSigningIn(false);
      return;
    }

    // Validate email format before attempting login
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      setError(emailValidation.errorMessage || 'Please enter a valid email address');
      setIsSigningIn(false);
      return;
    }

    try {
      await signIn(email, password);

      // Navigation will be handled by the useEffect above once permissions resolve
    } catch (error: unknown) {
      console.error('Login error:', error);

      // Clear referral data on critical auth errors
      const message = error && typeof error === 'object' && 'message' in error
        ? String(error.message)
        : '';
      const status = error && typeof error === 'object' && 'status' in error
        ? Number(error.status)
        : 0;

      if (message.includes('JWT') || message.includes('token') || status === 401) {
        ReferralStorageService.clearReferralData();
      }

      // Use enhanced error classification
      const classification = classifyAuthError(error);
      const formattedError = formatAuthError(classification);

      // Set error message with specific guidance
      setError(`${formattedError.title}. ${formattedError.description}`);

      // For email not confirmed, we still want to show the resend button
      // This is handled in the UI based on the error message content
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleResendConfirmation = async () => {
    try {
      setIsResendingEmail(true);
      setResendSuccess(false);
      
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });
      
      if (error) throw error;
      
      setResendSuccess(true);
    } catch (error: unknown) {
      console.error('Error resending confirmation email:', error);
      setError(`Failed to resend confirmation email: ${error instanceof Error ? error.message : ''}`);
    } finally {
      setIsResendingEmail(false);
    }
  };

  const handlePasswordReset = async () => {
    try {
      setIsResettingPassword(true);
      setResetSuccess(false);
      setError('');
      
      if (!email) {
        setError('Please enter your email address to reset your password');
        setIsResettingPassword(false);
        return;
      }
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      
      if (error) throw error;
      
      setResetSuccess(true);
      toast({
        title: "Password reset email sent",
        description: "Check your inbox for instructions to reset your password."
      });
    } catch (error: unknown) {
      console.error('Error requesting password reset:', error);
      setError(`Failed to send password reset email: ${error instanceof Error ? error.message : ''}`);
      toast({
        title: "Error",
        description: `Failed to send password reset email: ${error instanceof Error ? error.message : ''}`,
        variant: "destructive"
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold tracking-tight text-primary">The Mat Monster</h1>
          <h2 className="mt-6 text-3xl font-bold tracking-tight">
            Sign in to your account
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Or{" "}
            <Link to="/auth/signup" className="font-medium text-primary hover:text-primary/90">
              create a new account
            </Link>
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="animate-fadeIn">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error}
              {(error.includes('email has not been confirmed') || error.includes('Email not confirmed')) && (
                <div className="mt-2">
                  <Button
                    variant="secondary"
                    className="bg-white text-destructive hover:bg-white/90 border border-destructive font-medium"
                    onClick={handleResendConfirmation}
                    disabled={isResendingEmail}
                    size="sm"
                  >
                    {isResendingEmail ? 'Sending...' : 'Resend confirmation email'}
                  </Button>
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}
          
        {resendSuccess && (
          <Alert className="bg-green-50 border-green-200 mt-4">
            <AlertDescription className="text-green-800">
              Confirmation email has been resent. Please check your inbox and spam folder.
            </AlertDescription>
          </Alert>
        )}
          
        {resetSuccess && (
          <Alert className="bg-green-50 border-green-200 mt-4">
            <AlertDescription className="text-green-800">
              Password reset email has been sent. Please check your inbox for instructions.
            </AlertDescription>
          </Alert>
        )}

        {/* Enhanced form with iOS autofill support */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit} autoComplete="on">
          <div className="space-y-4 rounded-md">
            <div>
              <Label htmlFor="email">Email address</Label>
              <Input 
                id="email" 
                name="username"
                type="email" 
                autoComplete="username email"
                required 
                value={email} 
                onChange={e => setEmail(e.target.value)} 
                className="mt-1" 
                placeholder="Enter your email" 
                autoFocus 
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <button 
                  type="button" 
                  onClick={handlePasswordReset}
                  className="text-sm text-primary hover:text-primary/80 font-medium"
                  disabled={isResettingPassword}
                >
                  Forgot password?
                </button>
              </div>
              <Input 
                id="password" 
                name="password"
                type="password" 
                autoComplete="current-password"
                required 
                value={password} 
                onChange={e => setPassword(e.target.value)} 
                className="mt-1" 
                placeholder="Enter your password" 
              />
            </div>
          </div>

          <div>
            <Button type="submit" className="w-full" disabled={isSigningIn}>
              {isSigningIn ? 'Signing in...' : 'Sign in'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
